import { useMemo } from 'react';
import { postMedicalInsuranceDiscountCalculate } from 'modules/orders/api';
import {
  buildMedicalInsuranceDiscountCalculatePayload,
  getMedicalInsuranceInvoiceRows,
  isOrderEligibleForInsuranceDiscountCalculate,
} from 'modules/orders/medicalInsuranceDiscountCalculate';
import { useDebouncedEffect } from './useDebouncedEffect';

export function useMedicalInsuranceDiscountCalculate({
  orderDetails,
  dispensedOrderItems,
  productsAlternatives,
  isDispensedOrderItemsArray,
  onMedicalInsuranceDiscountCalculateResult,
  delayMs = 450,
}) {
  const medicalInsuranceDiscountTriggerKey = useMemo(() => {
    const rows = getMedicalInsuranceInvoiceRows(dispensedOrderItems);
    const rowSig = rows.map(r => ({
      o: r.orderItemKey,
      s: r.serviceKey,
      ps: r.productShapeId,
      q: r.quantity,
      p: r.price ?? r.servicePrice,
      rx: r.prescriptionKey,
    }));
    return JSON.stringify({ rowSig, alts: productsAlternatives || [] });
  }, [dispensedOrderItems, productsAlternatives]);

  useDebouncedEffect(
    () => {
      if (
        !isDispensedOrderItemsArray ||
        !isOrderEligibleForInsuranceDiscountCalculate(orderDetails) ||
        !Array.isArray(dispensedOrderItems)
      ) {
        return undefined;
      }

      const insuranceRows = getMedicalInsuranceInvoiceRows(dispensedOrderItems);
      if (!insuranceRows.length) {
        return undefined;
      }

      const payload = buildMedicalInsuranceDiscountCalculatePayload({
        orderDetails,
        dispensedOrderItems,
        productsAlternatives,
      });

      if (!payload) {
        onMedicalInsuranceDiscountCalculateResult?.(null);
        return undefined;
      }

      let isCancelled = false;
      postMedicalInsuranceDiscountCalculate(payload)
        .then(res => {
          if (isCancelled) return;
          const body = res?.data;
          if (body?.statusCode === 200 && body?.data != null) {
            onMedicalInsuranceDiscountCalculateResult?.(body);
          } else {
            onMedicalInsuranceDiscountCalculateResult?.(null);
          }
        })
        .catch(() => {
          if (isCancelled) return;
          onMedicalInsuranceDiscountCalculateResult?.(null);
        });

      return () => {
        isCancelled = true;
      };
    },
    [
      isDispensedOrderItemsArray,
      orderDetails,
      medicalInsuranceDiscountTriggerKey,
      dispensedOrderItems,
      productsAlternatives,
      onMedicalInsuranceDiscountCalculateResult,
    ],
    delayMs,
  );
}
